import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { EmptyState } from "@/components/feedback/EmptyState";
import { toast } from "@/components/feedback/ToastHost";
import { errorMessage } from "@/lib/errors";
import {
  listNotificationRules,
  updateNotificationRule,
} from "@/services/api/notifications";
import type { NotificationRule } from "@/services/api/types";

const CHANNELS = ["in_app", "email"] as const;

function RuleCard({ rule }: { rule: NotificationRule }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [channels, setChannels] = useState<string[]>(rule.channels);
  const [isEnabled, setIsEnabled] = useState(rule.is_enabled);
  const [dirty, setDirty] = useState(false);

  const save = useMutation({
    mutationFn: () =>
      updateNotificationRule(rule.id, {
        required_permission: rule.required_permission,
        channels,
        is_enabled: isEnabled,
      }),
    onSuccess: () => {
      toast.success(t("notifications.ruleSaved"));
      void queryClient.invalidateQueries({ queryKey: ["notification-rules"] });
      setDirty(false);
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const toggleChannel = (channel: string) => {
    setChannels((current) =>
      current.includes(channel)
        ? current.filter((item) => item !== channel)
        : [...current, channel],
    );
    setDirty(true);
  };

  return (
    <Card>
      <CardContent className="space-y-3 py-4">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Badge variant="secondary">{rule.event_type}</Badge>
            {rule.required_permission ? (
              <Badge variant="outline" className="font-mono text-xs">
                {rule.required_permission}
              </Badge>
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">{t("notifications.enabled")}</span>
            <Switch
              checked={isEnabled}
              onCheckedChange={(checked) => {
                setIsEnabled(checked);
                setDirty(true);
              }}
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {CHANNELS.map((channel) => (
            <Button
              key={channel}
              type="button"
              size="sm"
              variant={channels.includes(channel) ? "default" : "outline"}
              onClick={() => toggleChannel(channel)}
            >
              {channel}
            </Button>
          ))}
          {dirty ? (
            <Button size="sm" variant="ghost" onClick={() => save.mutate()} disabled={save.isPending}>
              {t("common.save")}
            </Button>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}

/** Admin: event-to-permission routing and delivery channels. */
export function NotificationRulesPage() {
  const { t } = useTranslation();
  const query = useQuery({
    queryKey: ["notification-rules"],
    queryFn: () => listNotificationRules(),
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">{t("nav.notificationRules")}</h1>
        <p className="text-sm text-muted-foreground">{t("notifications.rulesSubtitle")}</p>
      </div>

      {query.isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      ) : (query.data ?? []).length === 0 ? (
        <EmptyState title={t("notifications.emptyRules")} />
      ) : (
        <div className="space-y-3">
          {query.data?.map((rule) => (
            <RuleCard key={rule.id} rule={rule} />
          ))}
        </div>
      )}
    </div>
  );
}
